'use client';

import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cn } from '@/hen-ui/utils';
import {
  InlineCopyContext,
  isSafeInlineLinkHref,
} from '@/hen-ui/lib/inline-copy';
import { Icon, type IconName } from '../Icon';
import { Link } from '../Link';
import {
  listVariants,
  listItemVariants,
  interactiveRowBaseClass,
  interactiveRowLinkClass,
  listLinkColorTextClass,
  listIconGapClass,
  listRowPaddingClass,
  listEndRadiusClass,
  listRowHeightClass,
  hasCustomMarker,
  nativeListMarkerClass,
  resolveArrowDirection,
  LIST_ICON_SIZE_DEFAULT,
  LIST_ICON_GAP_DEFAULT,
  LIST_ROW_PADDING_DEFAULT,
  LIST_END_RADIUS_DEFAULT,
  LIST_ROW_HEIGHT_DEFAULT,
  type ArrowDirection,
  type BulletStyle,
  type ListLinkColor,
  type ListMarkerColor,
  type ListIconSize,
  type ListIconGap,
  type ListRowPadding,
  type ListEndRadius,
  type ListRowHeight,
  type ListVariantProps,
} from './list.variants';
import { ListItemMarker } from './ListItemMarker';

export type {
  ArrowDirection,
  BulletStyle,
  ListLinkColor,
  ListMarkerColor,
  ListIconSize,
  ListIconGap,
  ListRowPadding,
  ListEndRadius,
  ListRowHeight,
};

export interface ListProps
  extends React.HTMLAttributes<HTMLUListElement | HTMLOListElement>,
    ListVariantProps {
  asChild?: boolean;
  variant?: 'unordered' | 'ordered';
  bulletStyle?: BulletStyle;
  /**
   * Triangle marker direction when `bulletStyle` is `arrows`.
   * Defaults to `up` (green upward triangles). Only exact `down` (after trim) renders Cons.
   */
  arrowDirection?: ArrowDirection | string;
  /**
   * Overrides the marker color, including native disc and square markers.
   * When omitted, each marker keeps its built-in default (`bullet` primary,
   * `square` neutral, `check` success, `x` error, `arrows` directional
   * green/red, `number` and `q` inherit the row text). `body` uses the theme
   * body text color.
   */
  markerColor?: ListMarkerColor;
  /**
   * Draw a hairline rule between rows (none before the first/after the last).
   * Defaults off.
   */
  divided?: boolean;
  /**
   * Linked rows become full-width cells that tint on hover (no underline);
   * defaults off. Pairs with `divided` for a menu/contact block.
   */
  interactive?: boolean;
  /**
   * Color for row links and text (a curated subset of `Link` variants).
   * Leave unset to keep the default link color.
   */
  linkColor?: ListLinkColor;
  /**
   * Size of each row's leading icon. Defaults to `md` (24px); `sm-md` (20px)
   * suits tighter contact blocks.
   */
  iconSize?: ListIconSize;
  /**
   * Gap between a row's leading icon and its label. Defaults to `sm` (16px).
   * Distinct from `itemGap`, which spaces the rows apart.
   */
  iconGap?: ListIconGap;
  /**
   * Padding preset for interactive rows. Defaults to `comfortable` (20px L/R,
   * 16px T/B); `compact` is 16px L/R, 8px T/B. No effect on non-interactive rows.
   */
  rowPadding?: ListRowPadding;
  /**
   * Rounds the first/last interactive row corners to this radius so their focus
   * ring is not clipped inside a rounded `clipContent` card. Match the card's
   * `rounded` token. Defaults to `none`.
   */
  endRadius?: ListEndRadius;
  /**
   * Minimum height for interactive rows. Defaults to `auto` (sizes to padding
   * and content); `md` (48px) suits a seller-contact row, `lg` (56px)
   * a roomier menu. No effect on non-interactive rows.
   */
  rowHeight?: ListRowHeight;
  children: React.ReactNode;
}

export interface ListItemProps extends React.HTMLAttributes<HTMLLIElement> {
  asChild?: boolean;
  itemIndex?: number;
  /**
   * Leading Lucide icon before the row; blank renders text/link only.
   * Decorative (`aria-hidden`), so it stays out of the accessible name.
   */
  iconBefore?: IconName;
  /**
   * When set, the label renders inside a hen-ui `Link` as a semantic link.
   * A `tel:` URL is just an href — no call is placed.
   */
  href?: string;
  /** Passed through to the row `Link`: open the destination in a new tab. */
  openInNewTab?: boolean;
  children: React.ReactNode;
}

const ListContext = React.createContext<{
  bulletStyle: BulletStyle;
  arrowDirection: ArrowDirection;
  markerColor?: ListMarkerColor;
  divided: boolean;
  interactive: boolean;
  linkColor?: ListLinkColor;
  iconSize: ListIconSize;
  iconGap: ListIconGap;
  rowPadding: ListRowPadding;
  endRadius: ListEndRadius;
  rowHeight: ListRowHeight;
}>({
  bulletStyle: 'check',
  arrowDirection: 'up',
  markerColor: undefined,
  divided: false,
  interactive: false,
  linkColor: undefined,
  iconSize: LIST_ICON_SIZE_DEFAULT,
  iconGap: LIST_ICON_GAP_DEFAULT,
  rowPadding: LIST_ROW_PADDING_DEFAULT,
  endRadius: LIST_END_RADIUS_DEFAULT,
  rowHeight: LIST_ROW_HEIGHT_DEFAULT,
});

export const List = React.forwardRef<
  HTMLUListElement | HTMLOListElement,
  ListProps
>(
  (
    {
      className,
      asChild = false,
      variant = 'unordered',
      bulletStyle = 'check',
      itemGap,
      width,
      arrowDirection,
      markerColor,
      divided = false,
      interactive = false,
      linkColor,
      iconSize = LIST_ICON_SIZE_DEFAULT,
      iconGap = LIST_ICON_GAP_DEFAULT,
      rowPadding = LIST_ROW_PADDING_DEFAULT,
      endRadius = LIST_END_RADIUS_DEFAULT,
      rowHeight = LIST_ROW_HEIGHT_DEFAULT,
      children,
      ...props
    },
    ref,
  ) => {
    const resolvedArrowDirection =
      bulletStyle === 'arrows' ? resolveArrowDirection(arrowDirection) : 'up';
    const isOrdered = variant === 'ordered' || bulletStyle === 'number';
    const Comp = asChild ? Slot : isOrdered ? 'ol' : 'ul';
    const needsItemIndex = bulletStyle === 'number';

    const listChildren = needsItemIndex
      ? injectOrderedListItemIndexes(children)
      : children;

    return (
      <ListContext.Provider
        value={{
          bulletStyle,
          arrowDirection: resolvedArrowDirection,
          markerColor,
          divided,
          interactive,
          linkColor,
          iconSize,
          iconGap,
          rowPadding,
          endRadius,
          rowHeight,
        }}
      >
        <Comp
          className={cn(
            listVariants({ bulletStyle, itemGap, width, divided, interactive }),
            nativeListMarkerClass(bulletStyle, markerColor),
            className,
          )}
          ref={ref as React.Ref<HTMLUListElement & HTMLOListElement>}
          role='list'
          {...props}
        >
          {listChildren}
        </Comp>
      </ListContext.Provider>
    );
  },
);

List.displayName = 'List';

export const ListItem = React.forwardRef<HTMLLIElement, ListItemProps>(
  (
    {
      className,
      asChild = false,
      itemIndex = 0,
      iconBefore,
      href,
      openInNewTab,
      children,
      ...props
    },
    ref,
  ) => {
    const {
      bulletStyle,
      arrowDirection,
      markerColor,
      divided,
      interactive,
      linkColor,
      iconSize,
      iconGap,
      rowPadding,
      endRadius,
      rowHeight,
    } = React.useContext(ListContext);
    const iconGapClass = listIconGapClass[iconGap];
    const usesCustomMarker = hasCustomMarker(bulletStyle) && !interactive;
    // Links take the color as a variant (keeps hover/active); non-link content
    // takes the matching text class so icons and plain text follow suit.
    const rowTextColorClass = linkColor
      ? listLinkColorTextClass[linkColor]
      : undefined;
    // Drop unsafe hrefs (e.g. `javascript:`) to plain text; `Link` already guards nested rows.
    const nestedInlineCopy = React.useContext(InlineCopyContext);
    const safeHref =
      href != null && !nestedInlineCopy && !isSafeInlineLinkHref(href)
        ? undefined
        : href;

    if (asChild) {
      return (
        <Slot
          className={cn(
            listItemVariants({ divided, interactive }),
            rowTextColorClass,
            className,
          )}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    // Interactive rows make the whole cell the hover target: a link becomes a
    // full-width underline-free anchor; a plain row shares the padding only.
    if (interactive) {
      const rowPaddingClass = listRowPaddingClass[rowPadding];
      const rowHeightClass = listRowHeightClass[rowHeight];
      // List owns the leading icon (size + gap) rather than delegating to
      // `Link`'s `iconBefore`, which renders at the fixed Icon default.
      const leadingIcon = iconBefore ? (
        <Icon icon={iconBefore} size={iconSize} aria-hidden />
      ) : null;
      const interactiveRow = safeHref ? (
        <Link
          href={safeHref}
          variant={linkColor}
          openInNewTab={openInNewTab}
          underline='none'
          className={cn(
            interactiveRowBaseClass,
            iconGapClass,
            rowPaddingClass,
            rowHeightClass,
            interactiveRowLinkClass,
          )}
        >
          {leadingIcon}
          {children}
        </Link>
      ) : (
        <span
          className={cn(
            interactiveRowBaseClass,
            iconGapClass,
            rowPaddingClass,
            rowHeightClass,
          )}
        >
          {leadingIcon}
          {children}
        </span>
      );

      return (
        <li
          className={cn(
            listItemVariants({ divided, interactive: true }),
            listEndRadiusClass[endRadius],
            rowTextColorClass,
            className,
          )}
          ref={ref}
          {...props}
        >
          {interactiveRow}
        </li>
      );
    }

    // A row with an href becomes a semantic link (icon handled by Link); an
    // icon without an href draws the glyph inline before the plain label.
    let rowContent: React.ReactNode = children;
    if (safeHref) {
      // A leading icon needs an inline-flex host so the gap token applies;
      // Link's base `inline` display makes the gap inert, closing it. List
      // renders the icon itself (size + gap) rather than Link's `iconBefore`.
      rowContent = (
        <Link
          href={safeHref}
          variant={linkColor}
          openInNewTab={openInNewTab}
          className={
            iconBefore
              ? cn('inline-flex items-center', iconGapClass)
              : undefined
          }
        >
          {iconBefore && <Icon icon={iconBefore} size={iconSize} aria-hidden />}
          {children}
        </Link>
      );
    } else if (iconBefore) {
      rowContent = (
        <span className={cn('inline-flex items-center', iconGapClass)}>
          <Icon icon={iconBefore} size={iconSize} aria-hidden />
          {children}
        </span>
      );
    }

    return (
      <li
        className={cn(
          listItemVariants({ divided }),
          rowTextColorClass,
          usesCustomMarker && 'flex items-start gap-2',
          className,
        )}
        ref={ref}
        {...props}
      >
        {usesCustomMarker && (
          <ListItemMarker
            bulletStyle={bulletStyle}
            arrowDirection={arrowDirection}
            itemIndex={itemIndex}
            markerColor={markerColor}
          />
        )}
        {usesCustomMarker ? (
          <span className='min-w-0 flex-1'>{rowContent}</span>
        ) : (
          rowContent
        )}
      </li>
    );
  },
);

ListItem.displayName = 'ListItem';

type ListItemIndexProps = {
  itemIndex?: number;
};

function isInjectableListChild(
  child: React.ReactNode,
): child is React.ReactElement<ListItemIndexProps> {
  if (!React.isValidElement(child)) {
    return false;
  }

  if (child.type === React.Fragment) {
    return false;
  }

  // Intrinsic host nodes (`div`, `li`, …) reject unknown props like `itemIndex`.
  if (typeof child.type === 'string') {
    return false;
  }

  return true;
}

function injectOrderedListItemIndexes(
  children: React.ReactNode,
): React.ReactNode {
  let itemIndex = 0;

  return React.Children.map(children, (child) => {
    if (!isInjectableListChild(child)) {
      return child;
    }

    const nextIndex = child.props.itemIndex ?? itemIndex;
    itemIndex += 1;

    return React.cloneElement(child, {
      itemIndex: nextIndex,
    });
  });
}
